export interface NodeTypeOption {
    value: number;
    label: string;
    shortLabel: string;
    from: boolean;
    filter: boolean;
    params: boolean;
}

export const NODE_TYPE_OPTIONS: NodeTypeOption[] = [
    { value: 1, label: '1 - lista do zero', shortLabel: 'lista do zero', from: false, filter: true, params: true },
    { value: 2, label: '2 - lista baseada em números', shortLabel: 'lista c/ números', from: false, filter: true, params: true },
    { value: 3, label: '3 - filtrar lista', shortLabel: 'filtrar lista', from: true, filter: true, params: false },
    { value: 4, label: '4 - tranferir com máximo de repetições', shortLabel: 'máx. repetições', from: true, filter: false, params: true },
    { value: 5, label: '5 - tranferir com minímo de acertos', shortLabel: 'mín. acertos', from: true, filter: false, params: true },
    { value: 6, label: '6 - validar com lista base (máximo de acertos)', shortLabel: 'validar (máx.)', from: true, filter: false, params: true },
    { value: 7, label: '7 - reorganizar lista de forma randômica', shortLabel: 'randomizar', from: true, filter: false, params: false },
    { value: 8, label: '8 - combinações entre grupos com base em lista com id de grupo', shortLabel: 'comb. grupos', from: true, filter: true, params: false },
    { value: 9, label: '9 - validar com lista base (minímo de acertos)', shortLabel: 'validar (mín.)', from: true, filter: false, params: true },
    { value: 10, label: '10 - "lapida" combinações de lista com base em histórico para acertar o minímo', shortLabel: 'lapida mín.', from: true, filter: false, params: false },
    { value: 11, label: '11 - "lapida" combinações de lista com base em histórico para acertar o máximo', shortLabel: 'lapida máx.', from: true, filter: false, params: false },
    { value: 12, label: '12 - tranferir com intervalo de acertos (entre min e máx)', shortLabel: 'intervalo acert.', from: true, filter: false, params: true },
];

export const getNodeTypeOption = (type: number): NodeTypeOption =>
    NODE_TYPE_OPTIONS.find((item) => item.value === type) ?? {
        value: type,
        label: String(type),
        shortLabel: String(type),
        from: false,
        filter: false,
        params: false,
    };

export interface NodeTypeStepLinks {
    usesFromList: boolean;
    usesToList: boolean;
}

export const NODE_TYPE_STEP_LINKS: Record<number, NodeTypeStepLinks> = {
    1: { usesFromList: false, usesToList: true },
    2: { usesFromList: false, usesToList: true },
    3: { usesFromList: true, usesToList: true },
    4: { usesFromList: true, usesToList: true },
    5: { usesFromList: true, usesToList: true },
    6: { usesFromList: true, usesToList: true },
    7: { usesFromList: true, usesToList: true },
    8: { usesFromList: true, usesToList: true },
    9: { usesFromList: true, usesToList: true },
    10: { usesFromList: true, usesToList: true },
    11: { usesFromList: true, usesToList: true },
    12: { usesFromList: true, usesToList: true },
};

export const getNodeTypeStepLinks = (type: number): NodeTypeStepLinks =>
    NODE_TYPE_STEP_LINKS[type] ?? {
        usesFromList: false,
        usesToList: false,
    };

const hasParameterValue = (value: unknown) => {
    if (value === undefined || value === null || value === '') {
        return false;
    }

    if (Array.isArray(value)) {
        return value.length > 0;
    }

    return true;
};

export const countNodeParameters = (
    type: number,
    parameters: Record<string, unknown> = {}
) => {
    const typeOption = getNodeTypeOption(type);

    if (!typeOption.params) {
        return 0;
    }

    return getNodeTypeParameterDefinitions(type).filter((definition) =>
        hasParameterValue(parameters[definition.key])
    ).length;
};

export const countNodeFilters = (node: {
    filters?: unknown[];
    parameters?: { filters?: unknown[] };
}) => {
    if (Array.isArray(node.filters)) {
        return node.filters.length;
    }

    if (Array.isArray(node.parameters?.filters)) {
        return node.parameters.filters.length;
    }

    return 0;
};

export interface NodeEditFieldValidation {
    from?: string;
    fromListId?: string;
    toListId?: string;
    parameters?: Record<string, string>;
}

export const validateNodeEditRequiredFields = (
    editType: number,
    editFrom: number[],
    editParameterValues: Record<string, unknown> = {},
    editFromListId: number | null = null,
    editToListId: number | null = null
): { valid: boolean; message: string; fields: NodeEditFieldValidation } => {
    const typeOption = getNodeTypeOption(editType);
    const stepLinks = getNodeTypeStepLinks(editType);
    const fields: NodeEditFieldValidation = {};
    let message = '';

    if (typeOption.from && (!Array.isArray(editFrom) || editFrom.length === 0)) {
        fields.from = 'Selecione ao menos uma etapa de origem';
        message = fields.from;
    }

    if (stepLinks.usesFromList && (editFromListId === null || editFromListId === undefined)) {
        fields.fromListId = 'Selecione a lista de origem';
        if (!message) {
            message = fields.fromListId;
        }
    }

    if (stepLinks.usesToList && (editToListId === null || editToListId === undefined)) {
        fields.toListId = 'Selecione a lista de destino';
        if (!message) {
            message = fields.toListId;
        }
    }

    if (typeOption.params) {
        const missingParameters: Record<string, string> = {};

        getNodeTypeParameterDefinitions(editType).forEach((definition) => {
            if (!hasParameterValue(editParameterValues[definition.key])) {
                missingParameters[definition.key] = 'Campo obrigatório';
                if (!message) {
                    message = `Preencha o parâmetro "${definition.label}"`;
                }
            }
        });

        if (Object.keys(missingParameters).length > 0) {
            fields.parameters = missingParameters;
        }
    }

    return {
        valid: message === '',
        message,
        fields,
    };
};

export interface NodeTypeParameterDefinition {
    key: string;
    label: string;
    inputType: 'number' | 'array';
}

export const NODE_TYPE_PARAMETER_DEFINITIONS: Record<number, NodeTypeParameterDefinition[]> = {
    1: [
        { key: 'qtd_jogo', label: 'Quantidade de números por jogo', inputType: 'number' },
    ],
    2: [
        { key: 'qtd_jogo', label: 'Quantidade de números por jogo', inputType: 'number' },
        { key: 'numbers', label: 'Números', inputType: 'array' },
    ],
    3: [],
    4: [
        { key: 'max_rep', label: 'Máximo de repetições', inputType: 'number' },
    ],
    5: [
        { key: 'min_acert', label: 'Mínimo de acertos', inputType: 'number' },
    ],
    6: [
        { key: 'max_acert', label: 'Máximo de acertos', inputType: 'number' },
    ],
    7: [],
};

export const getNodeTypeParameterDefinitions = (type: number) =>
    NODE_TYPE_PARAMETER_DEFINITIONS[type] ?? [];

export const buildDefaultParameterValues = (
    type: number,
    existing: Record<string, unknown> = {}
) => {
    const definitions = getNodeTypeParameterDefinitions(type);

    return definitions.reduce<Record<string, unknown>>((acc, definition) => {
        if (existing[definition.key] !== undefined && existing[definition.key] !== null) {
            acc[definition.key] = existing[definition.key];
        }
        return acc;
    }, {});
};

export const sanitizeParameterValues = (
    type: number,
    values: Record<string, unknown> = {}
) => buildDefaultParameterValues(type, values);

export const formatParameterDescription = (
    type: number,
    values: Record<string, unknown> = {}
) => {
    const definitions = getNodeTypeParameterDefinitions(type);

    if (!definitions.length) {
        return 'Nenhum parâmetro para este tipo';
    }

    return definitions
        .map((definition) => {
            const value = values[definition.key];
            const displayValue = Array.isArray(value)
                ? `[${value.join(', ')}]`
                : value ?? '—';
            return `${definition.label}: ${displayValue}`;
        })
        .join(' | ');
};
